import React, { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { Checkmark16Icon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Link } from "@components/Link/Link";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { type IUseModalProps } from "@store/modal/modal";
import { useChubbBillInsuranceOrdersMutation } from "@services/insurance/mutations";
import { type IChubbBillInsuranceOrdersResponse } from "@services/insurance/types";
import {
  useBillInsuranceActivationSchema,
  type TBillInsuranceActivationSchema,
} from "./useBillInsuranceActivationSchema";

interface IBillInsuranceActivationModalProps extends IUseModalProps {
  chubbBillInsuranceOrdersData: IChubbBillInsuranceOrdersResponse;
  preselectedJointBillServiceId?: number;
}

export const BillInsuranceActivationModal: React.FC<
  IBillInsuranceActivationModalProps
> = ({
  chubbBillInsuranceOrdersData,
  preselectedJointBillServiceId,
  ...modalProps
}) => {
  const modal = useModal(modalProps);
  const { t } = useTranslation();
  const infoListItems = useTranslationArray(
    "billInsuranceActivationModal.info.list",
  );

  const jointBillServiceOptions = useMemo(
    () =>
      chubbBillInsuranceOrdersData.jointBillServices.map(
        (jointBillService) => ({
          label:
            jointBillService.displayName ??
            (jointBillService.isFakeAddress
              ? t("common.defaultAddress")
              : jointBillService.address),
          value: jointBillService.jointBillServiceId,
        }),
      ),
    [chubbBillInsuranceOrdersData.jointBillServices, t],
  );

  const schema = useBillInsuranceActivationSchema();

  const { control, handleSubmit, setError } =
    useForm<TBillInsuranceActivationSchema>({
      defaultValues: {
        firstName: chubbBillInsuranceOrdersData.customer.firstName,
        lastName: chubbBillInsuranceOrdersData.customer.lastName,
        email: chubbBillInsuranceOrdersData.customer.email,
        phone: chubbBillInsuranceOrdersData.customer.phoneNumber,
        jointBillServiceId:
          preselectedJointBillServiceId ?? jointBillServiceOptions[0]?.value,
        billInsuranceTerms: false,
        dataTransferToBillInsurer: false,
      },
      resolver: zodResolver(schema),
    });

  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync: orderChubbBillInsurance, isPending } =
    useChubbBillInsuranceOrdersMutation();

  const onSubmit: SubmitHandler<TBillInsuranceActivationSchema> = async (
    data,
  ) => {
    const response = await orderChubbBillInsurance({
      acceptedBillInsuranceTerms: data.billInsuranceTerms,
      acceptedDataTransferToBillInsurer: data.dataTransferToBillInsurer,
      dateOfBirth: data.dateOfBirth,
      jointBillServiceId: data.jointBillServiceId,
    }).catch((err) => isAxiosError(err) && setApiErrors(err, true));

    if (response) {
      toast.success(t("billInsuranceActivationModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("billInsuranceActivationModal.title")}
      subtitle={t("billInsuranceActivationModal.subtitle")}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            className="w-full"
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      className="md:max-w-2xl"
    >
      <div className="rounded-2xl bg-blue-light p-6">
        <span className="text-sm font-semibold">
          {t("billInsuranceActivationModal.info.title")}
        </span>
        <ul className="list-none p-0 pt-4">
          {infoListItems?.map((item, index) => (
            <li className="flex items-start pb-3" key={index}>
              <Checkmark16Icon className="h-5 w-4 min-w-[1rem]" />
              <div className="pl-2 text-sm">{item}</div>
            </li>
          ))}
        </ul>
        <Link
          className="mt-6 text-sm"
          to={t("billInsuranceActivationModal.info.moreInfo.link")}
        >
          {t("billInsuranceActivationModal.info.moreInfo.title")}
        </Link>
      </div>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 pt-5">
          <FormInput
            name="firstName"
            label={t(
              "billInsuranceActivationModal.form.fields.firstName.label",
            )}
            control={control}
            disabled
          />
          <FormInput
            name="lastName"
            label={t("billInsuranceActivationModal.form.fields.lastName.label")}
            control={control}
            disabled
          />
          <FormInput
            name="email"
            label={t("billInsuranceActivationModal.form.fields.email.label")}
            control={control}
            disabled
          />
          <FormInput
            name="phone"
            label={t("billInsuranceActivationModal.form.fields.phone.label")}
            control={control}
            disabled
          />
        </div>
        <FormSelect
          name="jointBillServiceId"
          label={t(
            "billInsuranceActivationModal.form.fields.jointBillServiceId.label",
          )}
          options={jointBillServiceOptions}
          control={control}
        />
        <div className="grid grid-cols-2">
          <FormDatepicker
            name="dateOfBirth"
            label={t(
              "billInsuranceActivationModal.form.fields.dateOfBirth.label",
            )}
            control={control}
            hint={t(
              "billInsuranceActivationModal.form.fields.dateOfBirth.hint",
            )}
          />
        </div>
        <div className="flex flex-col gap-4">
          <FormCheckbox
            name="billInsuranceTerms"
            label={parseHtml(
              t(
                "billInsuranceActivationModal.form.fields.billInsuranceTerms.label",
                {
                  link1: t(
                    "billInsuranceActivationModal.form.fields.billInsuranceTerms.link1",
                  ),
                  link2: t(
                    "billInsuranceActivationModal.form.fields.billInsuranceTerms.link2",
                  ),
                },
              ),
            )}
            control={control}
          />
          <FormCheckbox
            name="dataTransferToBillInsurer"
            label={parseHtml(
              t(
                "billInsuranceActivationModal.form.fields.dataTransferToBillInsurer.label",
                {
                  link: t(
                    "billInsuranceActivationModal.form.fields.dataTransferToBillInsurer.link",
                  ),
                },
              ),
            )}
            control={control}
          />
        </div>
      </div>
    </Modal>
  );
};
